import { UserGroupIcon } from '@heroicons/react/24/outline'
import type { Editor } from '@tiptap/react'
import { RichTextEditor } from 'components/RichTextEditor'
import { markersToHtml } from 'components/RichTextEditor/converters'
import { Mention } from 'components/RichTextEditor/mentionNode'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import type { MentionGroup, MentionUser } from './types'

type MentionState = { open: boolean; query: string; start: number; end: number }
const CLOSED: MentionState = { open: false, query: '', start: -1, end: -1 }

const EXTENSIONS = [Mention]

/**
 * The note body: the shared `RichTextEditor` plus the `@` typeahead that
 * `MentionTextarea` used to provide.
 *
 * Matching rules, ordering (departments above individuals), the 4/8 caps and
 * the keyboard behaviour are carried over from `MentionTextarea` unchanged, so
 * the box behaves the same; what is new is that the body can carry formatting.
 *
 * A pick inserts an atomic `mention` node rather than plain `@Name` text. The
 * node still serializes to the literal `@Name`, which is what keeps
 * `AddNote`'s save-time `content.includes('@' + name)` reconciliation, the
 * `stripHtmlTags` previews and the emailed copy working.
 */
export const MentionRichText = ({
  value,
  onChange,
  users,
  groups = [],
  onAddMention,
  onAddGroup,
  error = '',
  title = 'Note',
  required = false,
  additionalElements = null,
  placeholders = [],
  minHeight = 180,
  onEditorReady,
}: {
  value: string
  onChange: (value: string) => void
  users: MentionUser[]
  groups?: MentionGroup[]
  onAddMention: (email: string) => void
  onAddGroup?: (key: string) => void
  error?: string
  title?: string
  required?: boolean
  additionalElements?: JSX.Element | null
  placeholders?: string[]
  minHeight?: number
  onEditorReady?: (editor: Editor | null) => void
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [editor, setEditor] = useState<Editor | null>(null)
  const [mention, setMention] = useState<MentionState>(CLOSED)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [active, setActive] = useState(0)

  // The editor is driven by its own HTML; the stored value only seeds it and
  // re-syncs when the parent swaps it out (AI assist, Choose Template).
  const [editorHtml, setEditorHtml] = useState(() => markersToHtml(value || ''))
  const syncedValueRef = useRef<string>(value)

  useEffect(() => {
    if (value === syncedValueRef.current) return
    syncedValueRef.current = value
    setEditorHtml(markersToHtml(value || ''))
  }, [value])

  const handleChange = useCallback(
    (html: string) => {
      syncedValueRef.current = html
      setEditorHtml(html)
      onChange(html)
    },
    [onChange],
  )

  const q = mention.query.toLowerCase()
  // Departments sit above individuals: the whole point of the tag is to reach a
  // team, and a department name often prefixes the people in it.
  const matches = useMemo(() => {
    if (!mention.open) return []
    const groupMatches = groups.filter((g) => !q || g.title.toLowerCase().includes(q)).slice(0, 4)
    const userMatches = users
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 8)
    return [
      ...groupMatches.map((group) => ({ kind: 'group' as const, group })),
      ...userMatches.map((user) => ({ kind: 'user' as const, user })),
    ]
  }, [mention.open, q, groups, users])

  /** Look for an in-progress "@token" immediately before the caret. */
  const detect = useCallback((ed: Editor) => {
    const { selection } = ed.state
    if (!selection.empty) {
      setMention(CLOSED)
      return
    }
    const $from = selection.$from
    // '￼' stands in for any atom (an existing mention chip), so a chip
    // cannot be mistaken for part of the token being typed.
    const before = $from.parent.textBetween(0, $from.parentOffset, undefined, '￼')
    const m = before.match(/@([a-zA-Z0-9._@-]*)$/)
    if (m) {
      setMention({ open: true, query: m[1], start: $from.pos - m[0].length, end: $from.pos })
      setActive(0)
    } else {
      setMention((prev) => (prev.open ? CLOSED : prev))
    }
  }, [])

  useEffect(() => {
    if (!editor) return
    const run = () => detect(editor)
    editor.on('update', run)
    editor.on('selectionUpdate', run)
    const close = () => setTimeout(() => setMention(CLOSED), 150)
    editor.on('blur', close)
    return () => {
      editor.off('update', run)
      editor.off('selectionUpdate', run)
      editor.off('blur', close)
    }
  }, [editor, detect])

  // Position the dropdown just under the token being typed.
  useLayoutEffect(() => {
    if (!mention.open || !editor || !containerRef.current) return
    try {
      const caret = editor.view.coordsAtPos(mention.start)
      const box = containerRef.current.getBoundingClientRect()
      setPos({ top: caret.bottom - box.top + 4, left: caret.left - box.left })
    } catch {
      /* position out of date for one frame while the doc changes */
    }
  }, [mention, editor])

  const select = useCallback(
    (match: (typeof matches)[number]) => {
      if (!editor) return
      const label = match.kind === 'group' ? match.group.title : match.user.name
      const email = match.kind === 'group' ? '' : match.user.email
      editor
        .chain()
        .focus()
        .insertContentAt({ from: mention.start, to: mention.end }, [
          { type: 'mention', attrs: { name: label, email } },
          { type: 'text', text: ' ' },
        ])
        .run()
      if (match.kind === 'group') onAddGroup?.(match.group.key)
      else onAddMention(match.user.email)
      setMention(CLOSED)
    },
    [editor, mention.start, mention.end, onAddMention, onAddGroup],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!mention.open || !matches.length) return false
      if (event.key === 'ArrowDown') {
        setActive((i) => (i + 1) % matches.length)
        return true
      }
      if (event.key === 'ArrowUp') {
        setActive((i) => (i - 1 + matches.length) % matches.length)
        return true
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        select(matches[Math.min(active, matches.length - 1)])
        return true
      }
      if (event.key === 'Escape') {
        setMention(CLOSED)
        return true
      }
      return false
    },
    [mention.open, matches, active, select],
  )

  const handleEditorReady = useCallback(
    (ed: Editor | null) => {
      setEditor(ed)
      onEditorReady?.(ed)
    },
    [onEditorReady],
  )

  return (
    <div className="input-container">
      <label className="mb-1 flex items-center gap-2 text-[12px] text-gray-700">
        <span className="truncate" title={title}>
          {title}
        </span>
        {required && <span>*</span>}
        {additionalElements}
      </label>
      <div ref={containerRef} className="relative">
        <RichTextEditor
          value={editorHtml}
          onChange={handleChange}
          placeholders={placeholders}
          error={error}
          minHeight={minHeight}
          ariaLabel={title}
          variant="block"
          extensions={EXTENSIONS}
          handleKeyDown={handleKeyDown}
          onEditorReady={handleEditorReady}
        />

        {mention.open && matches.length > 0 && (
          <div
            className="absolute z-50 max-h-56 w-72 max-w-[90%] overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            {matches.map((match, idx) => (
              <div
                key={match.kind === 'group' ? `group-${match.group.key}` : match.user.email}
                className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm ${
                  idx === active ? 'bg-shade-blue/10' : 'hover:bg-gray-100'
                }`}
                onMouseEnter={() => setActive(idx)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  select(match)
                }}
              >
                {match.kind === 'group' ? (
                  <>
                    <span className="flex items-center gap-1.5 font-medium text-gray-900">
                      <UserGroupIcon className="h-4 w-4 text-shade-blue" />
                      {match.group.title}
                    </span>
                    <span className="text-xs text-gray-500">
                      {match.group.count} {match.group.count === 1 ? 'person' : 'people'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-medium text-gray-900">{match.user.name}</span>
                    <span className="text-xs text-gray-500">{match.user.email}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
